import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import L from "leaflet";
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from "react-leaflet";

import { Icon } from "../components/Icon.jsx";
import { notifyDriver } from "./driver-notifications.js";

const DEFAULT_CENTER = [-6.966, 110.416];
const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const ATTRIBUTION = "© OpenStreetMap contributors";

function validCoordinate(point) {
  const latitude = Number(point?.latitude);
  const longitude = Number(point?.longitude);
  return Number.isFinite(latitude) && Number.isFinite(longitude);
}

function latLng(point) {
  return [Number(point.latitude), Number(point.longitude)];
}

function geometryPositions(route) {
  const coordinates = route?.geometry?.coordinates;
  if (!Array.isArray(coordinates)) return [];
  return coordinates
    .map(([longitude, latitude]) => [Number(latitude), Number(longitude)])
    .filter(([latitude, longitude]) => Number.isFinite(latitude) && Number.isFinite(longitude));
}

function routeMarker(label, tone = "pending", selected = false) {
  return L.divIcon({
    className: `driver-route-workspace-marker is-${tone}${selected ? " is-selected" : ""}`,
    html: `<span>${label}</span>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18],
  });
}

function FitRoute({ points, version }) {
  const map = useMap();
  useEffect(() => {
    const valid = points.filter(validCoordinate);
    if (!valid.length) return;
    map.fitBounds(L.latLngBounds(valid.map(latLng)), { padding: [34, 34], maxZoom: 16 });
  }, [map, points, version]);
  return null;
}

function FocusSelectedStop({ point }) {
  const map = useMap();
  useEffect(() => {
    if (!validCoordinate(point)) return;
    map.flyTo(latLng(point), Math.max(map.getZoom(), 15), { animate: true, duration: 0.35 });
  }, [map, point]);
  return null;
}

function formatDistance(value) {
  const meters = Number(value || 0);
  if (!Number.isFinite(meters) || meters <= 0) return "—";
  return meters >= 1000
    ? `${new Intl.NumberFormat("id-ID", { maximumFractionDigits: 1 }).format(meters / 1000)} km`
    : `${Math.round(meters)} m`;
}

function formatDuration(value) {
  const seconds = Number(value || 0);
  if (!Number.isFinite(seconds) || seconds <= 0) return "—";
  const minutes = Math.round(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  if (!hours) return `${minutes} menit`;
  return remaining ? `${hours} jam ${remaining} menit` : `${hours} jam`;
}

function haversineMeters(left, right) {
  if (!validCoordinate(left) || !validCoordinate(right)) return 0;
  const toRadians = (value) => (Number(value) * Math.PI) / 180;
  const earthRadiusM = 6_371_000;
  const latitudeDelta = toRadians(Number(right.latitude) - Number(left.latitude));
  const longitudeDelta = toRadians(Number(right.longitude) - Number(left.longitude));
  const leftLatitude = toRadians(left.latitude);
  const rightLatitude = toRadians(right.latitude);
  const haversine = (Math.sin(latitudeDelta / 2) ** 2)
    + (Math.cos(leftLatitude) * Math.cos(rightLatitude) * (Math.sin(longitudeDelta / 2) ** 2));
  return 2 * earthRadiusM * Math.asin(Math.sqrt(haversine));
}

function buildOfflineRoutePreview(anchor, stops, returnDepot) {
  const points = [anchor, ...stops, returnDepot].filter(validCoordinate);
  if (points.length < 2) return null;
  const distanceM = points.slice(1).reduce(
    (total, point, index) => total + haversineMeters(points[index], point),
    0,
  );
  const travelSeconds = (distanceM / 1000 / 32) * 3600;
  const stopServiceSeconds = Math.max(0, stops.length) * 7 * 60;
  return {
    source: "OFFLINE_ESTIMATE",
    distanceM,
    durationS: travelSeconds + stopServiceSeconds,
    orderedStopKeys: stops.map((stop) => stop.key),
    geometry: {
      type: "LineString",
      coordinates: points.map((point) => [Number(point.longitude), Number(point.latitude)]),
    },
    calculatedAt: new Date().toISOString(),
  };
}

function signedMetric(value, formatter) {
  const number = Number(value || 0);
  if (!Number.isFinite(number) || number === 0) return null;
  return `${number > 0 ? "+" : "−"}${formatter(Math.abs(number))}`;
}

function movedStopCount(initialKeys, orderedKeys) {
  return orderedKeys.reduce((count, key, index) => count + (initialKeys[index] === key ? 0 : 1), 0);
}

function spreadOverlappingPoints(points) {
  const groups = new Map();
  for (const point of points) {
    if (!validCoordinate(point)) continue;
    const key = `${Number(point.latitude).toFixed(4)}:${Number(point.longitude).toFixed(4)}`;
    const group = groups.get(key) || [];
    group.push(point);
    groups.set(key, group);
  }

  const rendered = new Map();
  for (const group of groups.values()) {
    if (group.length === 1) {
      rendered.set(group[0].key, group[0]);
      continue;
    }
    group.forEach((point, index) => {
      const angle = (Math.PI * 2 * index) / group.length;
      const radius = 0.00024;
      rendered.set(point.key, {
        ...point,
        displayLatitude: Number(point.latitude) + (Math.sin(angle) * radius),
        displayLongitude: Number(point.longitude) + (Math.cos(angle) * radius),
      });
    });
  }
  return rendered;
}

function displayLatLng(point) {
  return [
    Number(point.displayLatitude ?? point.latitude),
    Number(point.displayLongitude ?? point.longitude),
  ];
}

function SortableRouteStop({ stop, sequence, disabled, selected, onSelect }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stop.key,
    disabled,
  });

  return (
    <article
      className={`driver-route-editor-stop ${isDragging ? "is-dragging" : ""} ${selected ? "is-selected" : ""}`}
      data-stop-key={stop.key}
      onClick={() => onSelect(stop.key)}
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <button
        aria-label={disabled ? `Aktifkan Atur manual untuk memindahkan ${stop.customerName}` : `Tekan dan tahan untuk memindahkan ${stop.customerName}`}
        className="driver-route-editor-handle"
        disabled={disabled}
        type="button"
        {...attributes}
        {...listeners}
      >
        <span /><span /><span />
      </button>
      <span className="driver-route-editor-sequence">{sequence}</span>
      <span className="driver-route-editor-copy">
        <strong>{stop.customerName}</strong>
        <small>{[stop.area, stop.documentNumbers?.join(", ")].filter(Boolean).join(" · ") || "Detail belum tersedia"}</small>
      </span>
    </article>
  );
}

function UnsavedExitDialog({ busy, canSave, onCancel, onDiscard, onSave }) {
  return (
    <div className="driver-route-exit-dialog-backdrop" role="presentation">
      <section aria-modal="true" className="driver-route-exit-dialog" role="dialog">
        <header>
          <span className="driver-placeholder-icon"><Icon name="warning" size={22} /></span>
          <div>
            <h2>Perubahan belum disimpan</h2>
            <p>Urutan rute yang baru belum diterapkan.</p>
          </div>
        </header>
        <div className="driver-route-exit-dialog__actions">
          <button className="driver-secondary-button" disabled={busy} onClick={onCancel} type="button">Tetap di halaman</button>
          <button className="driver-secondary-button" disabled={busy} onClick={onDiscard} type="button">Buang perubahan</button>
          <button className="driver-primary-button" disabled={busy || !canSave} onClick={onSave} type="button">{busy ? "Menyimpan..." : "Simpan urutan"}</button>
        </div>
      </section>
    </div>
  );
}

export function DriverRouteWorkspace({
  payload,
  online,
  actionBusy,
  onClose,
  onOptimize,
  onPreview,
  onSave,
  onReload,
  onDiscardDraft,
  onDirtyChange,
  onRegisterExitRequest,
}) {
  const workspace = payload?.routeWorkspace || {};
  const editableStops = Array.isArray(workspace.editableStops) ? workspace.editableStops : [];
  const lockedStops = Array.isArray(workspace.lockedStops) ? workspace.lockedStops : [];
  const routePrefixStops = Array.isArray(workspace.routePrefixStops)
    ? workspace.routePrefixStops
    : lockedStops;
  const stopByKey = useMemo(() => new Map(editableStops.map((stop) => [stop.key, stop])), [editableStops]);
  const serverKeys = useMemo(
    () => workspace.currentEditableStopKeys || editableStops.map((stop) => stop.key),
    [editableStops, workspace.currentEditableStopKeys],
  );
  const routeDraft = payload?.routeDraft || null;
  const draftKeys = Array.isArray(routeDraft?.stopKeys) ? routeDraft.stopKeys.map(String) : [];
  const draftCompatible = Boolean(routeDraft)
    && Number(routeDraft.expectedRouteVersion || 1) === Number(workspace.routeVersion || 1)
    && draftKeys.length === serverKeys.length
    && draftKeys.every((key) => serverKeys.includes(key));
  const draftConflict = Boolean(routeDraft) && (routeDraft.syncStatus === "CONFLICT" || !draftCompatible);
  const initialKeys = draftCompatible ? draftKeys : serverKeys;
  const headingRef = useRef(null);
  const baselinePreviewRef = useRef(payload?.route || null);
  const [baselineKeys, setBaselineKeys] = useState(initialKeys);
  const [orderedKeys, setOrderedKeys] = useState(initialKeys);
  const [preview, setPreview] = useState(payload?.route || null);
  const [comparison, setComparison] = useState(null);
  const [method, setMethod] = useState("MANUAL_DRAG");
  const [manualMode, setManualMode] = useState(false);
  const [busyMode, setBusyMode] = useState("");
  const [error, setError] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [selectedStopKey, setSelectedStopKey] = useState(null);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [pendingSync, setPendingSync] = useState(Boolean(routeDraft && draftCompatible && routeDraft.syncStatus !== "CONFLICT"));
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 260, tolerance: 9 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const changed = orderedKeys.join("|") !== baselineKeys.join("|");
  const route = preview || payload?.route;
  const routePositions = useMemo(() => geometryPositions(route), [route]);
  const orderedStops = orderedKeys.map((key) => stopByKey.get(key)).filter(Boolean);
  const routePrefixCount = Number(workspace.routePrefixCount ?? routePrefixStops.length);
  const mapStops = [
    ...routePrefixStops
      .filter((stop) => stop.key !== workspace.anchor?.key)
      .map((stop, index) => ({ ...stop, mapSequence: stop.actualSequence || index + 1, locked: true })),
    ...orderedStops.map((stop, index) => ({ ...stop, mapSequence: routePrefixCount + index + 1, locked: false })),
  ];
  const spreadPoints = useMemo(() => spreadOverlappingPoints(mapStops), [mapStops]);
  const displayedMapStops = mapStops.map((stop) => spreadPoints.get(stop.key) || stop);
  const selectedStop = displayedMapStops.find((stop) => stop.key === selectedStopKey) || null;
  const fitPoints = [workspace.anchor, ...displayedMapStops, workspace.returnDepot].filter(validCoordinate);
  const center = fitPoints[0] ? latLng(fitPoints[0]) : DEFAULT_CENTER;
  const requiresAcknowledgement = Boolean(workspace.loadingConfirmed && changed);
  const canSave = workspace.canSave && changed && !draftConflict && (!requiresAcknowledgement || acknowledged) && !busyMode && !actionBusy;
  const movedCount = movedStopCount(initialKeys, orderedKeys);
  const metricMode = route?.source === "OFFLINE_ESTIMATE"
    ? "Perkiraan offline"
    : changed
      ? "Preview"
      : pendingSync
        ? "Menunggu sinkronisasi"
        : "Rute aktif";
  const baseRoute = baselinePreviewRef.current || payload?.route;
  const distanceDelta = Number(route?.distanceM || 0) - Number(baseRoute?.distanceM || 0);
  const durationDelta = Number(route?.durationS || 0) - Number(baseRoute?.durationS || 0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    window.requestAnimationFrame(() => headingRef.current?.focus({ preventScroll: true }));
  }, []);

  useEffect(() => {
    setBaselineKeys(initialKeys);
    setOrderedKeys(initialKeys);
    setPreview(payload?.route || null);
    baselinePreviewRef.current = payload?.route || null;
    setPendingSync(Boolean(routeDraft && draftCompatible && routeDraft.syncStatus !== "CONFLICT"));
    setComparison(null);
    setAcknowledged(Boolean(routeDraft?.acknowledgeLoadingImpact));
    setManualMode(false);
    setSelectedStopKey(null);
  }, [payload?.routeWorkspace?.routeVersion, initialKeys.join("|"), routeDraft?.updatedAt]);

  useEffect(() => {
    let cancelled = false;
    if (!baselineKeys.length || workspace.missingCoordinateStops?.length) return undefined;
    const baselineStops = baselineKeys.map((key) => stopByKey.get(key)).filter(Boolean);
    if (!online) {
      const localPreview = buildOfflineRoutePreview(workspace.anchor, baselineStops, workspace.returnDepot);
      if (localPreview) {
        baselinePreviewRef.current = localPreview;
        setPreview(localPreview);
      }
      return undefined;
    }
    setBusyMode("preview");
    onPreview(baselineKeys)
      .then((result) => {
        if (cancelled) return;
        baselinePreviewRef.current = result.preview;
        setPreview(result.preview);
      })
      .catch((nextError) => {
        if (!cancelled) setError(nextError.message || "Pratinjau rute belum dapat dihitung.");
      })
      .finally(() => {
        if (!cancelled) setBusyMode("");
      });
    return () => { cancelled = true; };
  }, [online, payload?.routeWorkspace?.routeVersion, baselineKeys.join("|")]);

  useEffect(() => {
    onDirtyChange?.(changed);
  }, [changed, onDirtyChange]);

  useEffect(() => {
    onRegisterExitRequest?.(() => setShowExitDialog(true));
    return () => onRegisterExitRequest?.(null);
  }, [onRegisterExitRequest]);

  useEffect(() => {
    function beforeUnload(event) {
      if (!changed) return;
      event.preventDefault();
      event.returnValue = "";
    }
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [changed]);

  useEffect(() => {
    function refreshWhenVisible() {
      if (document.visibilityState !== "visible" || changed || busyMode) return;
      onReload().catch(() => {});
    }
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => document.removeEventListener("visibilitychange", refreshWhenVisible);
  }, [busyMode, changed, onReload]);

  const selectStop = useCallback((key, { scroll = false } = {}) => {
    setSelectedStopKey(key);
    if (!scroll) return;
    window.requestAnimationFrame(() => {
      document.querySelector(`[data-stop-key="${String(key).replaceAll('"', '\\"')}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, []);

  async function runPreview(keys) {
    setError("");
    if (!online) {
      const localStops = keys.map((key) => stopByKey.get(key)).filter(Boolean);
      const localPreview = buildOfflineRoutePreview(workspace.anchor, localStops, workspace.returnDepot);
      if (localPreview) setPreview(localPreview);
      setComparison(null);
      setMethod("MANUAL_DRAG");
      return;
    }
    setBusyMode("preview");
    try {
      const result = await onPreview(keys);
      setPreview(result.preview);
      setComparison(null);
      setMethod("MANUAL_DRAG");
    } catch (nextError) {
      setError(nextError.message || "Pratinjau rute belum dapat dihitung.");
    } finally {
      setBusyMode("");
    }
  }

  async function handleDragEnd(event) {
    const { active, over } = event;
    if (!manualMode || !over || active.id === over.id) return;
    const oldIndex = orderedKeys.indexOf(active.id);
    const newIndex = orderedKeys.indexOf(over.id);
    const nextKeys = arrayMove(orderedKeys, oldIndex, newIndex);
    setOrderedKeys(nextKeys);
    setAcknowledged(false);
    setSelectedStopKey(String(active.id));
    await runPreview(nextKeys);
  }

  async function optimize() {
    setBusyMode("optimize");
    setError("");
    setManualMode(false);
    try {
      const result = await onOptimize();
      const nextKeys = result.preview.orderedStopKeys;
      const hasChange = nextKeys.join("|") !== baselineKeys.join("|");
      setOrderedKeys(nextKeys);
      setPreview(result.preview);
      setComparison(result.comparison || null);
      setMethod("AUTO_OPTIMIZED");
      setAcknowledged(false);

      if (!hasChange) {
        notifyDriver("Tidak ada perubahan yang perlu disimpan.", {
          id: "driver-route-optimized",
          tone: "info",
          title: "Urutan saat ini sudah optimal",
          duration: 4200,
        });
      } else if (result.comparison?.hasImprovement) {
        notifyDriver(`Jarak berkurang ${formatDistance(result.comparison.distanceSavedM)} dan estimasi lebih cepat ${formatDuration(result.comparison.durationSavedS)}.`, {
          id: "driver-route-optimized",
          tone: "success",
          title: "Rute berhasil dioptimalkan",
          duration: 5600,
        });
      } else {
        notifyDriver("Urutan baru sudah ditampilkan. Periksa lalu tekan Simpan urutan.", {
          id: "driver-route-optimized",
          tone: "success",
          title: "Rute berhasil dioptimalkan",
          duration: 5200,
        });
      }
    } catch (nextError) {
      const message = nextError.message || "Periksa koneksi dan coba kembali.";
      setError(message);
      notifyDriver(message, { tone: "error", title: "Rute belum dapat dioptimalkan" });
    } finally {
      setBusyMode("");
    }
  }

  async function save({ closeAfter = false } = {}) {
    if (!canSave) return false;
    setBusyMode("save");
    setError("");
    try {
      const nextPayload = await onSave({
        stopKeys: orderedKeys,
        expectedRouteVersion: workspace.routeVersion,
        method,
        anchor: workspace.anchor,
        acknowledgeLoadingImpact: acknowledged,
      });
      if (nextPayload?.queued) {
        const nextKeys = [...orderedKeys];
        setBaselineKeys(nextKeys);
        baselinePreviewRef.current = preview;
        setPendingSync(true);
        setComparison(null);
        setManualMode(false);
        onDirtyChange?.(false);
        notifyDriver("Urutan tersimpan di perangkat dan akan disinkronkan saat koneksi stabil.", {
          tone: "warning",
          title: "Menunggu sinkronisasi",
          duration: 5200,
        });
        if (closeAfter) onClose({ force: true });
        return true;
      }
      const nextWorkspace = nextPayload?.routeWorkspace || {};
      const nextInitialKeys = nextWorkspace.currentEditableStopKeys || orderedKeys;
      baselinePreviewRef.current = nextPayload?.route || preview;
      setPreview(nextPayload?.route || preview);
      setBaselineKeys(nextInitialKeys);
      setOrderedKeys(nextInitialKeys);
      setPendingSync(false);
      setComparison(null);
      setAcknowledged(false);
      setManualMode(false);
      onDirtyChange?.(false);
      if (closeAfter) onClose({ force: true });
      return true;
    } catch (nextError) {
      setError(nextError.message || "Urutan rute belum dapat disimpan.");
      return false;
    } finally {
      setBusyMode("");
    }
  }

  function resetChanges() {
    setOrderedKeys(baselineKeys);
    setPreview(baselinePreviewRef.current || payload?.route || null);
    setComparison(null);
    setAcknowledged(false);
    setManualMode(false);
    setSelectedStopKey(null);
  }

  function attemptClose() {
    if (changed) {
      setShowExitDialog(true);
      return;
    }
    onClose();
  }

  function discardAndClose() {
    resetChanges();
    onDirtyChange?.(false);
    setShowExitDialog(false);
    window.requestAnimationFrame(() => onClose({ force: true }));
  }

  const anchorCaption = workspace.anchor?.type === "ACTIVE_STOP"
    ? "Titik yang sedang dijalankan"
    : workspace.anchor?.type === "LAST_VISITED_STOP"
      ? "Titik terakhir dikunjungi"
      : "Titik awal perjalanan";

  return (
    <div className={`driver-route-workspace-page ${manualMode ? "is-manual-mode" : ""} ${route?.source === "OFFLINE_ESTIMATE" ? "is-offline-estimate" : ""}`.trim()}>
      <header className="driver-route-workspace-header">
        <button aria-label="Kembali ke detail Fleet Order" onClick={attemptClose} type="button">
          <Icon name="chevronLeft" size={22} />
        </button>
        <div>
          <strong ref={headingRef} tabIndex="-1">Atur Urutan Rute</strong>
          <small>{payload?.fleetOrder?.foNumber}</small>
        </div>
        <span className={online ? "is-online" : "is-offline"}>{online ? "Online" : "Offline"}</span>
      </header>

      {!online && (
        <section className="driver-route-workspace-alert is-warning">
          <Icon name="warning" size={18} />
          <div><strong>Mode offline</strong><p>Optimasi otomatis membutuhkan internet. Urutan manual tetap dapat disimpan di perangkat.</p></div>
        </section>
      )}

      {pendingSync && !draftConflict && (
        <section className="driver-route-workspace-alert is-pending" role="status">
          <Icon name="sync" size={18} />
          <div><strong>Urutan menunggu sinkronisasi</strong><p>Perubahan sudah aman di perangkat dan akan dikirim otomatis saat koneksi stabil.</p></div>
        </section>
      )}

      {draftConflict && (
        <section className="driver-route-workspace-alert is-danger" role="alert">
          <Icon name="warning" size={18} />
          <div>
            <strong>Draft rute perlu ditinjau</strong>
            <p>{routeDraft?.lastErrorMessage || "Urutan rute sudah berubah di perangkat lain. Gunakan data terbaru sebelum mengatur ulang."}</p>
            <button disabled={Boolean(busyMode)} onClick={onDiscardDraft} type="button">Gunakan rute terbaru</button>
          </div>
        </section>
      )}

      <section className="driver-route-workspace-map-card">
        <div className="driver-route-workspace-map">
          <MapContainer center={center} zoom={11} scrollWheelZoom>
            <TileLayer attribution={ATTRIBUTION} url={TILE_URL} />
            <FitRoute points={fitPoints} version={`${orderedKeys.join("|")}:${preview?.calculatedAt || "saved"}`} />
            <FocusSelectedStop point={selectedStop} />
            {routePositions.length > 1 && (
              <Polyline pathOptions={{ color: "#16777a", opacity: 0.9, weight: 5 }} positions={routePositions} />
            )}
            {validCoordinate(workspace.anchor) && (
              <Marker icon={routeMarker("M", "anchor")} position={latLng(workspace.anchor)}>
                <Popup><strong>Mulai dari</strong><p>{workspace.anchor.label}</p></Popup>
              </Marker>
            )}
            {displayedMapStops.map((stop) => validCoordinate(stop) && (
              <Marker
                eventHandlers={{ click: () => selectStop(stop.key, { scroll: true }) }}
                icon={routeMarker(
                  stop.locked && stop.status === "EN_ROUTE" ? "→" : stop.locked ? "✓" : stop.mapSequence,
                  stop.locked ? "locked" : stop.mapSequence === routePrefixCount + 1 ? "first" : "pending",
                  stop.key === selectedStopKey,
                )}
                key={stop.key}
                position={displayLatLng(stop)}
              >
                <Popup><strong>{stop.mapSequence}. {stop.customerName}</strong><p>{stop.area || "Area belum tersedia"}</p></Popup>
              </Marker>
            ))}
            {validCoordinate(workspace.returnDepot) && (
              <Marker icon={routeMarker("G", "depot")} position={latLng(workspace.returnDepot)}>
                <Popup><strong>Kembali ke</strong><p>{workspace.returnDepot.depotName}</p></Popup>
              </Marker>
            )}
          </MapContainer>
        </div>
        <div className="driver-route-workspace-endpoints">
          <article><span>Mulai dari</span><strong>{workspace.anchor?.label || "—"}</strong><small>{anchorCaption}</small></article>
          <article><span>Kembali ke</span><strong>{workspace.returnDepot?.depotName || "—"}</strong><small>Titik akhir perjalanan</small></article>
        </div>
        <div className={`driver-route-workspace-metrics ${changed ? "is-preview" : ""}`}>
          <span><small>Jarak</small><strong>{formatDistance(route?.distanceM)}</strong></span>
          <span><small>Estimasi</small><strong>{formatDuration(route?.durationS)}</strong></span>
          <span><small>Titik tersisa</small><strong>{orderedStops.length}</strong></span>
          <em>{metricMode}</em>
        </div>
      </section>

      <section className="driver-route-workspace-controls">
        <div>
          <span className="driver-eyebrow">Atur rute</span>
          <h1>Susun urutan titik tersisa</h1>
          <p>Titik selesai dan titik yang sedang dijalankan tetap terkunci.</p>
        </div>
        <div className="driver-route-workspace-mode-actions">
          <button className="driver-primary-button" disabled={!online || !workspace.canOptimize || Boolean(busyMode)} onClick={optimize} type="button">
            <Icon name="route" size={18} /> {busyMode === "optimize" ? "Mengoptimalkan..." : "Optimalkan otomatis"}
          </button>
          <button
            aria-pressed={manualMode}
            className={`driver-secondary-button ${manualMode ? "is-active" : ""}`}
            disabled={Boolean(busyMode)}
            onClick={() => setManualMode((current) => !current)}
            type="button"
          >
            <Icon name="orders" size={18} /> {manualMode ? "Selesai mengatur" : "Atur manual"}
          </button>
        </div>
        {manualMode && <p className="driver-route-manual-help"><Icon name="info" size={16} /> Tekan dan tahan ikon, lalu geser titik. Sentuh area nama untuk scroll.</p>}
        {changed && (
          <div className="driver-route-preview-summary" role="status">
            <Icon name="warning" size={18} />
            <span>
              <strong>Preview urutan baru</strong>
              <small>{movedCount} titik berubah · Belum disimpan</small>
              {(distanceDelta || durationDelta) ? (
                <small>
                  {signedMetric(distanceDelta, formatDistance) ? `Jarak ${signedMetric(distanceDelta, formatDistance)}` : ""}
                  {distanceDelta && durationDelta ? " · " : ""}
                  {signedMetric(durationDelta, formatDuration) ? `Estimasi ${signedMetric(durationDelta, formatDuration)}` : ""}
                </small>
              ) : null}
            </span>
          </div>
        )}
        {comparison?.hasImprovement && (
          <div className="driver-route-optimization-result">
            <Icon name="check" size={18} />
            <span><strong>Rute lebih efisien</strong><small>Hemat {formatDistance(comparison.distanceSavedM)} · {formatDuration(comparison.durationSavedS)}</small></span>
          </div>
        )}
      </section>

      {lockedStops.length > 0 && (
        <details className="driver-route-locked-stops">
          <summary>{lockedStops.length} titik sudah dikunci</summary>
          <div>{lockedStops.map((stop) => <span key={stop.key}>{stop.customerName}</span>)}</div>
        </details>
      )}

      <section className="driver-route-workspace-list-card">
        <div className="driver-section-heading">
          <div><h2>Urutan titik tersisa</h2><p>{manualMode ? "Geser melalui ikon di sisi kiri." : "Tekan Atur manual untuk mengubah urutan."}</p></div>
          <span>{editableStops.length} titik</span>
        </div>
        {editableStops.length ? (
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} sensors={sensors}>
            <SortableContext items={orderedKeys} strategy={verticalListSortingStrategy}>
              <div className="driver-route-editor-list">
                {orderedStops.map((stop, index) => (
                  <SortableRouteStop
                    disabled={!manualMode || Boolean(busyMode) || actionBusy}
                    key={stop.key}
                    onSelect={selectStop}
                    selected={stop.key === selectedStopKey}
                    sequence={routePrefixCount + index + 1}
                    stop={stop}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="driver-task-empty"><strong>Tidak ada titik tersisa</strong><p>Semua titik sudah memiliki hasil akhir.</p></div>
        )}
      </section>

      {workspace.missingCoordinateStops?.length > 0 && (
        <section className="driver-route-workspace-alert is-danger">
          <Icon name="warning" size={18} />
          <div><strong>Koordinat belum lengkap</strong><p>{workspace.missingCoordinateStops.map((stop) => stop.customerName).join(", ")}</p></div>
        </section>
      )}

      {requiresAcknowledgement && (
        <label className="driver-route-loading-impact">
          <input checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} type="checkbox" />
          <span><strong>Urutan berubah setelah loading dikonfirmasi</strong><small>Saya sudah memastikan susunan barang masih sesuai dengan urutan terbaru.</small></span>
        </label>
      )}

      {error && (
        <section className="driver-route-workspace-alert is-danger">
          <Icon name="warning" size={18} />
          <div><strong>Rute belum dapat diperbarui</strong><p>{error}</p><button onClick={onReload} type="button">Muat ulang data</button></div>
        </section>
      )}

      {changed ? (
        <>
          <div className="driver-route-unsaved-banner" role="status">
            <Icon name="warning" size={17} />
            <span><strong>Perubahan belum disimpan</strong><small>Urutan ini masih berupa preview.</small></span>
          </div>
          <footer className="driver-route-workspace-footer">
            <button className="driver-secondary-button" disabled={Boolean(busyMode)} onClick={resetChanges} type="button">Urungkan</button>
            <button className="driver-primary-button" disabled={!canSave} onClick={() => save()} type="button"><Icon name="check" size={18} /> {busyMode === "save" ? "Menyimpan..." : "Simpan urutan"}</button>
          </footer>
        </>
      ) : (
        <div className="driver-route-no-change" role="status"><Icon name="check" size={17} /> Belum ada perubahan</div>
      )}

      {showExitDialog && (
        <UnsavedExitDialog
          busy={busyMode === "save"}
          canSave={canSave}
          onCancel={() => setShowExitDialog(false)}
          onDiscard={discardAndClose}
          onSave={() => save({ closeAfter: true })}
        />
      )}
    </div>
  );
}
